'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface WeatherData {
  condition: string;
  temperature: number;
  humidity: number;
  dustLevel: string;
  cleaningRecommendation: string;
  icon: string;
}

export default function WeatherIntegration() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSeason, setCurrentSeason] = useState('');

  useEffect(() => {
    setIsHydrated(true);
    const month = new Date().getMonth();
    if (month >= 2 && month <= 4) setCurrentSeason('Spring');
    else if (month >= 5 && month <= 7) setCurrentSeason('Summer');
    else if (month >= 8 && month <= 10) setCurrentSeason('Fall');
    else setCurrentSeason('Winter');
  }, []);

  const mockWeatherData: WeatherData = {
    condition: 'Sunny',
    temperature: 78,
    humidity: 15,
    dustLevel: 'Moderate',
    cleaningRecommendation: 'Ideal conditions for patio cleaning. Low humidity ensures quick drying.',
    icon: 'SunIcon'
  };

  const seasonalNeeds = [
    {
      season: 'Spring',
      needs: ['Dust storm cleanup', 'Pollen removal', 'Pre-summer deep clean'],
      priority: 'High'
    },
    {
      season: 'Summer',
      needs: ['Monsoon stain prevention', 'UV damage protection', 'Pool area maintenance'],
      priority: 'Critical'
    },
    {
      season: 'Fall',
      needs: ['Post-monsoon restoration', 'Leaf debris removal', 'Winter preparation'],
      priority: 'High'
    },
    {
      season: 'Winter',
      needs: ['Mild weather maintenance', 'Holiday entertaining prep', 'Annual deep clean'],
      priority: 'Moderate'
    }
  ];

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-brand p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="h-24 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const currentSeasonData = seasonalNeeds.find(s => s.season === currentSeason) || seasonalNeeds[0];

  return (
    <div className="bg-card rounded-lg shadow-brand p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-headline font-bold text-xl text-primary">
          Phoenix Weather & Cleaning Insights
        </h3>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground font-body">
          <Icon name="CloudIcon" size={20} className="text-secondary" />
          <span>Live Conditions</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Weather */}
        <div className="bg-background rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground font-body mb-1">Current Conditions</p>
              <p className="font-headline font-bold text-2xl text-primary">
                {mockWeatherData.temperature}°F
              </p>
            </div>
            <Icon name={mockWeatherData.icon as any} size={48} className="text-warning" variant="solid" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground font-body">Condition:</span>
              <span className="font-body font-medium text-foreground">{mockWeatherData.condition}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground font-body">Humidity:</span>
              <span className="font-body font-medium text-foreground">{mockWeatherData.humidity}%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground font-body">Dust Level:</span>
              <span className="font-body font-medium text-warning">{mockWeatherData.dustLevel}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-start space-x-2">
              <Icon name="LightBulbIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground font-body">
                {mockWeatherData.cleaningRecommendation}
              </p>
            </div>
          </div>
        </div>

        {/* Seasonal Needs */}
        <div className="bg-background rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground font-body mb-1">Current Season</p>
              <p className="font-headline font-bold text-xl text-primary">{currentSeason}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${
              currentSeasonData.priority === 'Critical' ? 'bg-error/10 text-error' :
              currentSeasonData.priority === 'High'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
            }`}>
              {currentSeasonData.priority} Priority
            </span>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-body font-medium text-foreground mb-3">
              Recommended Services:
            </p>
            {currentSeasonData.needs.map((need, index) => (
              <div key={index} className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success flex-shrink-0 mt-1" variant="solid" />
                <span className="text-sm text-muted-foreground font-body">{need}</span>
              </div>
            ))}
          </div>

          <button className="w-full mt-4 bg-accent text-accent-foreground px-4 py-2 rounded-md text-sm font-body font-medium hover:bg-accent/90 shadow-cta-glow transition-brand">
            Schedule Seasonal Service
          </button>
        </div>
      </div>
    </div>
  );
}